import { SxProps, Theme } from '@mui/material';

type Styles = {
  container: (blockWidth: number) => SxProps<Theme>;
  item: SxProps<Theme>;
  divider: SxProps<Theme>;
  listContainer: SxProps<Theme>;
  listItem: (theme: Theme) => SxProps<Theme>;
  icon: (theme: Theme) => SxProps<Theme>;
  selectedListItem: (theme: Theme) => SxProps<Theme>;
  selectedIcon: (theme: Theme) => SxProps<Theme>;
  completedListItem: (theme: Theme) => SxProps<Theme>;
  disabledListItem: SxProps<Theme>;
  disabledIcon: (theme: Theme) => SxProps<Theme>;
  doneIconSelected: SxProps<Theme>;
  title: SxProps<Theme>;
  sidebarHeaderContainer: SxProps<Theme>;
  closedSidebarListItem: SxProps<Theme>;
  burgerButton: SxProps<Theme>;
  lineCommon: (theme: Theme) => SxProps<Theme>;
  line1: (isOpened: boolean) => SxProps<Theme>;
  line2: (isOpened: boolean) => SxProps<Theme>;
  line3: (isOpened: boolean) => SxProps<Theme>;
};

export const styles: Styles = {
  container: (blockWidth: number) => ({
    zIndex: 300,
    position: 'fixed',
    top: 0,
    left: 0,
    height: '100vh',
    background: 'white',
    color: '#0166b3',
    overflow: 'hidden',
    width: blockWidth,
    flexShrink: 0,
    transition: 'width 0.3s ease',
  }),
  item: {
    display: 'flex',
    flex: 1,
    maxWidth: 'calc(50% - 20px)',
    alignItems: 'center',
  },
  divider: (theme: Theme) => ({
    background: theme.palette.grey[100],
    marginY: theme.spacing(4),
    marginX: theme.spacing(2),
    border: 'none',
    height: 2,
  }),
  listContainer: (theme: Theme) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    overflow: 'auto',
    overflowX: 'hidden',
    height: 'calc(100% - 100px)',
    [theme.breakpoints.down('sm')]: {
      height: 'calc(100% - 140px)',
    },
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(2),
    mb: theme.spacing(1),
    scrollbarWidth: 'thin',
    scrollbarGutter: 'stable',
    '&::-webkit-scrollbar': {
      width: theme.spacing(1),
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#bdbdbd',
      borderRadius: 4,
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: '#9e9e9e',
    },
  }),
  listItem: (theme: Theme) => ({
    border: '1px solid transparent',
    borderRadius: 0.5,
    '&:hover': { borderColor: theme.palette.primary.main },
    gap: theme.spacing(2),
    maxHeight: theme.spacing(6),
    minHeight: theme.spacing(6),
    cursor: 'pointer',
    transition: 'width 0.3s ease',
  }),
  selectedListItem: (theme: Theme) => ({
    '&:before': {
      background: theme.palette.primary.main,
      height: '100%',
      width: theme.spacing(1),
      position: 'absolute',
      content: '""',
      left: '-26px',
      borderTopRightRadius: theme.spacing(0.5),
      borderBottomRightRadius: theme.spacing(0.5),
    },
    background: theme.palette.primary.main,
  }),
  completedListItem: (theme: Theme) => ({
    background: theme.palette.secondary.main,
  }),
  icon: (theme: Theme) => ({
    '& .stroke': {
      stroke: theme.palette.primary.main,
      fill: 'transparent',
    },
    '& .fill': { fill: theme.palette.primary.main },
  }),
  selectedIcon: (theme: Theme) => ({
    '& .stroke': {
      stroke: theme.palette.common.white,
      fill: theme.palette.primary.main,
    },
    '& .fill': { fill: theme.palette.common.white },
  }),
  disabledListItem: {
    '&:hover': { borderColor: 'transparent' },
    cursor: 'default',
  },
  disabledIcon: (theme: Theme) => ({
    '& .stroke': {
      stroke: theme.palette.text.disabled,
      fill: theme.palette.common.white,
    },
    '& .fill': { fill: theme.palette.text.disabled },
  }),
  doneIconSelected: (theme: Theme) => ({
    color: theme.palette.common.white,
  }),
  title: (theme: Theme) => ({
    fontSize: '22px',
    fontWeight: '900',
    fontFamily: 'Arial, sans-serif',
    letterSpacing: 'normal',
    textTransform: 'uppercase',
    color: theme.palette.primary.dark,
    whiteSpace: 'nowrap',
    pl: theme.spacing(1),
  }),
  sidebarHeaderContainer: (theme: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(3),
    cursor: 'pointer',
    [theme.breakpoints.down('sm')]: {
      mt: theme.spacing(5),
    },
  }),
  closedSidebarListItem: {
    width: 50,
    justifyContent: 'center',
    padding: '0px !important',
    gap: 0,
  },
  burgerButton: (theme: Theme) => ({
    position: 'fixed',
    top: '25px',
    left: theme.spacing(4),
    gap: 0.5,
    display: 'none',
    flexDirection: 'column',
    zIndex: 500,
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
    },
  }),
  lineCommon: (theme: Theme) => ({
    display: 'flex',
    height: '2px',
    backgroundColor: theme.palette.primary.main,
    transition: '0.4s',
  }),
  line1: (isOpened: boolean) => ({
    width: '20px',
    transform: isOpened ? 'rotate(45deg)  translate(5px, 5px)' : 'rotate(0deg)',
  }),
  line2: (isOpened: boolean) => ({
    width: '20px',
    opacity: isOpened ? 0 : 1,
    transition: 'none',
  }),
  line3: (isOpened: boolean) => ({
    width: isOpened ? '20px' : '10px',
    transform: isOpened ? 'rotate(-45deg)  translate(3px, -3.5px)' : 'rotate(0deg)',
  }),
};
